import React, { useCallback, useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import Dropdown from "@/components/ui/dropdown";
import "./LibraryShareCustomFields.scss";
import { staffManagementApi } from "@/features/staff-management/services/staff-management.api";
import { fetchDepartments } from "@/features/announcements/services/departments.api";

const SEARCH_DEBOUNCE_MS = 300;
const USERS_PAGE_SIZE = 30;
const DEPARTMENTS_PAGE_SIZE = 50;

export interface LibraryShareCustomFieldsProps {
  sharedUserIds: string[];
  sharedDepartmentIds: string[];
  onUsersChange: (ids: string[]) => void;
  onDepartmentsChange: (ids: string[]) => void;
  /** Modal açıkken mount edildiği için isOpen ile ilk yükleme tetiklenebilir */
  isVisible?: boolean;
}

const LibraryShareCustomFields: React.FC<LibraryShareCustomFieldsProps> = ({
  sharedUserIds,
  sharedDepartmentIds,
  onUsersChange,
  onDepartmentsChange,
  isVisible = true,
}) => {
  const { t } = useTranslation();
  const [userOptions, setUserOptions] = useState<Array<{ label: string; value: string }>>([]);
  const [departmentOptions, setDepartmentOptions] = useState<Array<{ label: string; value: string }>>([]);
  const [userSearch, setUserSearch] = useState("");
  const [departmentSearch, setDepartmentSearch] = useState("");
  const [debouncedUserSearch, setDebouncedUserSearch] = useState("");
  const [debouncedDepartmentSearch, setDebouncedDepartmentSearch] = useState("");
  const [usersLoading, setUsersLoading] = useState(false);
  const [departmentsLoading, setDepartmentsLoading] = useState(false);
  const userSearchRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const departmentSearchRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (userSearchRef.current) clearTimeout(userSearchRef.current);
    userSearchRef.current = setTimeout(() => {
      setDebouncedUserSearch(userSearch);
      userSearchRef.current = null;
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (userSearchRef.current) clearTimeout(userSearchRef.current);
    };
  }, [userSearch]);

  useEffect(() => {
    if (departmentSearchRef.current) clearTimeout(departmentSearchRef.current);
    departmentSearchRef.current = setTimeout(() => {
      setDebouncedDepartmentSearch(departmentSearch);
      departmentSearchRef.current = null;
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (departmentSearchRef.current) clearTimeout(departmentSearchRef.current);
    };
  }, [departmentSearch]);

  const fetchUsers = useCallback(async (search: string) => {
    setUsersLoading(true);
    try {
      const { data } = await staffManagementApi.getAll({
        page: 1,
        pageSize: USERS_PAGE_SIZE,
        filters: { search: search.trim() || undefined },
      });
      setUserOptions(
        data.map((u) => ({
          label: u.name || u.email || u.id,
          value: u.id,
        }))
      );
    } catch {
      setUserOptions([]);
    } finally {
      setUsersLoading(false);
    }
  }, []);

  const fetchDepartmentsList = useCallback(async (search: string) => {
    setDepartmentsLoading(true);
    try {
      const list = await fetchDepartments({
        page: 1,
        limit: DEPARTMENTS_PAGE_SIZE,
        search: search.trim() || undefined,
      });
      setDepartmentOptions(list.map((d) => ({ label: d.name, value: d.id })));
    } catch {
      setDepartmentOptions([]);
    } finally {
      setDepartmentsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isVisible) return;
    fetchUsers(debouncedUserSearch);
  }, [isVisible, debouncedUserSearch, fetchUsers]);

  useEffect(() => {
    if (!isVisible) return;
    fetchDepartmentsList(debouncedDepartmentSearch);
  }, [isVisible, debouncedDepartmentSearch, fetchDepartmentsList]);

  const userSelectedLabel =
    sharedUserIds.length === 0
      ? undefined
      : sharedUserIds.length === 1
        ? userOptions.find((o) => o.value === sharedUserIds[0])?.label ?? sharedUserIds[0]
        : t("library.share.customUsersSelected", { count: sharedUserIds.length });

  const departmentSelectedLabel =
    sharedDepartmentIds.length === 0
      ? undefined
      : sharedDepartmentIds.length === 1
        ? departmentOptions.find((o) => o.value === sharedDepartmentIds[0])?.label ?? sharedDepartmentIds[0]
        : t("library.share.customDepartmentsSelected", { count: sharedDepartmentIds.length });

  return (
    <div className="library-share-custom-fields">
      <Dropdown
        label={t("library.share.searchUser")}
        placeholder={usersLoading ? t("common.loading") : t("library.share.searchUserPlaceholder")}
        options={userOptions}
        value={sharedUserIds}
        selectedLabel={userSelectedLabel}
        onSelect={(v) => onUsersChange(Array.isArray(v) ? v : [v])}
        onSearch={(v) => setUserSearch(v)}
        searchable
        multiple
        menuPosition="top"
      />
      <Dropdown
        label={t("library.share.searchDepartment")}
        placeholder={departmentsLoading ? t("common.loading") : t("library.share.searchDepartmentPlaceholder")}
        options={departmentOptions}
        value={sharedDepartmentIds}
        selectedLabel={departmentSelectedLabel}
        onSelect={(v) => onDepartmentsChange(Array.isArray(v) ? v : [v])}
        onSearch={(v) => setDepartmentSearch(v)}
        searchable
        multiple
        menuPosition="top"
      />
    </div>
  );
};

export default LibraryShareCustomFields;
